/**
 * Captura de origem (first-touch) do cadastro.
 *
 * Espelha deliberadamente `src/lib/referral.ts`: mesma estrutura de
 * localStorage + TTL, mesmo padrão de `capture` / `read` / `consume`, mesmo
 * try/catch em todo acesso ao storage. Se um dia os dois convergirem, o caminho
 * é óbvio — mas hoje eles guardam coisas diferentes (referral guarda um código
 * de indicação; aqui guardamos o pacote de mídia inteiro).
 *
 * POR QUE FIRST-TOUCH E NÃO ÚLTIMO
 * A pergunta do negócio é "o que trouxe esta pessoa", e quem chega pela LP com
 * `?utm_source=lp&fbclid=...` e depois navega para `/register` perderia a origem
 * na segunda página se sobrescrevêssemos. Por isso `captureFirstTouch` NÃO
 * sobrescreve um registro válido já existente.
 *
 * POR QUE O `document.referrer` INTERNO É DESCARTADO
 * Numa SPA o referrer da própria origem é só navegação interna. Guardá-lo faria
 * todo mundo parecer vindo de `atividadepronta.com.br`, escondendo a origem real.
 *
 * LGPD: é dado de primeira parte, vinculado à conta, usado para medição interna
 * de aquisição e diagnóstico de cadastro. Nada aqui é repassado a terceiros, e
 * nenhum IP é coletado. A chave vive 30 dias no navegador e está declarada na
 * política de cookies na categoria de necessários/funcionais — a mesma leitura
 * que já se aplica a `ap_referral_code_v1`.
 */

const STORAGE_KEY = 'ap_attribution_v1'
const STORAGE_TTL_DAYS = 30

/** Limite por campo. UTM legítimo é curto; o resto é lixo ou ataque. */
const MAX_FIELD = 300
const MAX_URL = 500

export type AttributionSource = 'signup_metadata' | 'client_topup' | 'backfill' | 'unknown'

export interface Attribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer?: string
  landing_page?: string
  /** gclid / fbclid / msclkid, só as chaves presentes. */
  click_ids?: Record<string, string>
  captured_at: string
}

interface StoredAttribution extends Attribution {
  capturedAt: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid'] as const

/** Corta, apara e remove caracteres de controle. */
const clean = (value: string | null | undefined, max = MAX_FIELD): string | undefined => {
  if (!value) return undefined
  // eslint-disable-next-line no-control-regex
  const stripped = value.replace(/[\u0000-\u001F\u007F]/g, '').trim()
  if (!stripped) return undefined
  return stripped.slice(0, max)
}

/**
 * O referrer só interessa quando é externo. Numa SPA, navegar de `/` para
 * `/register` produz um referrer da própria origem — guardá-lo transformaria
 * todo cadastro em "veio do próprio site".
 */
const externalReferrer = (referrer: string | null | undefined): string | undefined => {
  const value = clean(referrer, MAX_URL)
  if (!value) return undefined
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    if (origin && value.startsWith(origin)) return undefined
  } catch {
    // sem window (SSR/teste): mantém o valor como veio
  }
  return value
}

/** Extrai os sinais de origem de uma query string. */
export const parseAttribution = (
  search: string,
  referrer?: string | null,
  landingPage?: string | null
): Attribution => {
  const params = new URLSearchParams(search)
  const result: Attribution = { captured_at: new Date().toISOString() }

  for (const key of UTM_KEYS) {
    const value = clean(params.get(key))
    if (value) result[key] = value
  }

  const clickIds: Record<string, string> = {}
  for (const key of CLICK_ID_KEYS) {
    const value = clean(params.get(key))
    if (value) clickIds[key] = value
  }
  if (Object.keys(clickIds).length > 0) result.click_ids = clickIds

  const ref = externalReferrer(referrer)
  if (ref) result.referrer = ref

  const landing = clean(landingPage, MAX_URL)
  if (landing) result.landing_page = landing

  return result
}

/**
 * Tem algum sinal aproveitável?
 *
 * `landing_page` NÃO conta como sinal, e essa distinção é o que mantém a tabela
 * honesta: toda visita tem uma página de entrada, então aceitá-la geraria linha
 * para 100% dos usuários — todas classificadas como "direto" — e "ausência de
 * linha = sem dados" deixaria de significar qualquer coisa. A página de entrada
 * é CONTEXTO que acompanha um sinal (de onde veio), não o sinal em si.
 *
 * A RPC `upsert_user_acquisition` aplica exatamente a mesma regra no banco.
 */
export const hasSignal = (attribution: Attribution | null | undefined): boolean => {
  if (!attribution) return false
  return Boolean(
    attribution.utm_source ||
      attribution.utm_medium ||
      attribution.utm_campaign ||
      attribution.utm_content ||
      attribution.utm_term ||
      attribution.referrer ||
      (attribution.click_ids && Object.keys(attribution.click_ids).length > 0)
  )
}

/** Grava o primeiro toque. Não sobrescreve um registro válido existente. */
export const captureFirstTouch = (
  search: string,
  referrer?: string | null,
  landingPage?: string | null
): void => {
  if (typeof window === 'undefined') return
  if (readAttribution()) return

  const attribution = parseAttribution(search, referrer, landingPage)
  if (!hasSignal(attribution)) return

  try {
    const payload: StoredAttribution = { ...attribution, capturedAt: attribution.captured_at }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // ignora — navegação privada, storage bloqueado, cota estourada
  }
}

export const readAttribution = (): Attribution | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAttribution
    const ageDays = (Date.now() - new Date(parsed.capturedAt).getTime()) / (1000 * 60 * 60 * 24)
    if (!Number.isFinite(ageDays) || ageDays > STORAGE_TTL_DAYS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    // `capturedAt` e detalhe do storage; `captured_at` e o campo do payload.
    const attribution: Attribution = { ...parsed, captured_at: parsed.captured_at }
    delete (attribution as Partial<StoredAttribution>).capturedAt
    return attribution
  } catch {
    return null
  }
}

export const consumeAttribution = (): Attribution | null => {
  const attribution = readAttribution()
  if (attribution && typeof window !== 'undefined') {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignora
    }
  }
  return attribution
}

/**
 * O que enviar no cadastro. Se o storage veio vazio — Safari com ITP, janela
 * privada, aparelho diferente do da primeira visita — cai para o ÚLTIMO toque
 * lido da URL atual, em vez de desistir da atribuição.
 */
export const attributionForSignup = (): Attribution | null => {
  const stored = consumeAttribution()
  if (stored) return stored

  if (typeof window === 'undefined') return null
  const fallback = parseAttribution(
    window.location.search,
    typeof document !== 'undefined' ? document.referrer : null,
    window.location.pathname
  )
  return hasSignal(fallback) ? fallback : null
}

/**
 * Recorte leve e não-identificante do dispositivo, para o painel responder
 * "em que aparelho isso aconteceu" sem depender do fingerprint completo.
 *
 * De propósito NÃO chama `enhancedFingerprintService`: aquele roda canvas e
 * áudio, custa caro no primeiro load e existe para antifraude, não para
 * atribuição. O fingerprint completo continua em `device_fingerprints`, e o
 * painel o lê de lá.
 */
export const deviceSnapshot = (): Record<string, string> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return {}
  try {
    return {
      user_agent: (navigator.userAgent || '').slice(0, MAX_FIELD),
      platform: (navigator.platform || '').slice(0, 80),
      screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    }
  } catch {
    return {}
  }
}

/** Fuso e idioma do navegador — o recorte regional honesto, sem geo-IP. */
export const localeSnapshot = (): { timezone?: string; locale?: string } => {
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
      locale: navigator.language || undefined,
    }
  } catch {
    return {}
  }
}
